import React, { useEffect, useMemo, useState } from 'react';
import { FaFileExcel, FaSpinner, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { ExcelDownload } from '../../utils/excel.utils';
import { SearchableSelect, SelectWithSearch, type SelectOption } from '../../utils/select-with-search';
import { formatDate } from '../activities/edit';
import { useAuth } from '../../provider/auth.provider';
import { dateRangeInputBounds, firstDateRangeError } from '../../utils/date-range.utils';

interface SiteReportRow {
  [key: string]: any;
}

interface SiteFileEntry {
  id: number;
  file_name: string;
  file_path: string;
  file_size?: number | string | null;
  file_type?: string;
  upload_stage?: string;
  uploaded_by?: number;
  uploaded_at?: string;
}

interface WorkflowLogEntry {
  action_type?: string | null;
  to_status?: string | null;
  performed_at?: string | null;
  performed_by?: string | number | null;
  remarks?: string | null;
  [key: string]: any;
}

interface SiteReportFilters {
  search: string;
  project_id: string;
  department_id: string;
  activity_id: string;
  state_circle_id: string;
  district_id: string;
  site_engineer_userid: string;
  survey_workflow_status_list: string;
  association_type: string;
  site_id: string;
  site_name: string;
  claim_status_list: string;
  travel_mode: string;
  terrain_type: string;
  is_project_claim_submitted: string;
  billing_status_list: string;
  wcc_status: string;
  invoice_status: string;
  is_billing_done: string;
  report_submitted: string;
  is_po_received: string;
  client_allocation_date_from: string;
  client_allocation_date_to: string;
  site_engineer_assign_date_from: string;
  site_engineer_assign_date_to: string;
  site_engineer_work_date_from: string;
  site_engineer_work_date_to: string;
  allocation_date_from: string;
  allocation_date_to: string;
  survey_date_from: string;
  survey_date_to: string;
  workflow_action_type: string;
  workflow_to_status: string;
  workflow_performed_at_from: string;
  workflow_performed_at_to: string;
  sort_by: string;
  sort_order: 'ASC' | 'DESC';
}

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const DEFAULT_FILTERS: SiteReportFilters = {
  search: '',
  project_id: '',
  department_id: '',
  activity_id: '',
  state_circle_id: '',
  district_id: '',
  site_engineer_userid: '',
  survey_workflow_status_list: '',
  association_type: '',
  site_id: '',
  site_name: '',
  claim_status_list: '',
  travel_mode: '',
  terrain_type: '',
  is_project_claim_submitted: '',
  billing_status_list: '',
  wcc_status: '',
  invoice_status: '',
  is_billing_done: '',
  report_submitted: '',
  is_po_received: '',
  client_allocation_date_from: '',
  client_allocation_date_to: '',
  site_engineer_assign_date_from: '',
  site_engineer_assign_date_to: '',
  site_engineer_work_date_from: '',
  site_engineer_work_date_to: '',
  allocation_date_from: '',
  allocation_date_to: '',
  survey_date_from: '',
  survey_date_to: '',
  workflow_action_type: '',
  workflow_to_status: '',
  workflow_performed_at_from: '',
  workflow_performed_at_to: '',
  sort_by: 'created_at',
  sort_order: 'DESC',
};

const NUMERIC_FILTER_KEYS: Array<keyof SiteReportFilters> = [
  'project_id',
  'department_id',
  'activity_id',
  'state_circle_id',
  'district_id',
  'site_engineer_userid',
];

const BOOLEAN_FILTER_KEYS: Array<keyof SiteReportFilters> = [
  'is_project_claim_submitted',
  'is_billing_done',
  'report_submitted',
  'is_po_received',
];

type DropdownOption = {
  value: string | number;
  label: string;
};

const WORKFLOW_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'site_created', label: 'Site Created' },
  { value: 'engineer_assigned', label: 'Engineer Assigned' },
  { value: 'field_work_in_progress', label: 'Field Work In Progress' },
  { value: 'data_uploaded', label: 'Data Uploaded' },
  { value: 'field_work_completed', label: 'Field Work Completed' },
  { value: 'ta_review_pending', label: 'TA Review Pending' },
  { value: 'ta_review_completed', label: 'TA Review Completed' },
  { value: 'ta_rejected', label: 'TA Rejected' },
  { value: 'mis_report_assign_pending', label: 'MIS Report Assign Pending' },
  { value: 'report_maker_assigned', label: 'Report Maker Assigned' },
  { value: 'report_in_progress', label: 'Report In Progress' },
  { value: 'report_completed', label: 'Report Completed' },
  { value: 'report_maker_rejected', label: 'Report Maker Rejected' },
  { value: 'validator_assigned', label: 'Validator Assigned' },
  { value: 'validation_in_progress', label: 'Validation In Progress' },
  { value: 'validation_completed', label: 'Validation Completed' },
  { value: 'validation_rejected', label: 'Validation Rejected' },
  { value: 'report_finalized', label: 'Report Finalized' },
  { value: 'report_sent_to_client', label: 'Report Sent To Client' },
];

const CLAIM_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const BILLING_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'wcc_raised', label: 'WCC Raised' },
  { value: 'wcc_approved', label: 'WCC Approved' },
  { value: 'invoice_raised', label: 'Invoice Raised' },
  { value: 'invoice_submitted', label: 'Invoice Submitted' },
  { value: 'paid', label: 'Paid' },
];

const WCC_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const INVOICE_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
];

const WORKFLOW_ACTION_OPTIONS: DropdownOption[] = [
  { value: 'assignment', label: 'Assignment' },
  { value: 'status_change', label: 'Status Change' },
  { value: 'approval', label: 'Approval' },
  { value: 'data_upload', label: 'Data Upload' },
  { value: 'location_verification', label: 'Location Verification' },
];

const EXCLUDE_FIELD_SEGMENTS = new Set(['id']);
const HIDDEN_TABLE_COLUMNS = new Set([
  'workflow_status_dates.site_created',
  'workflow_status_dates.engineer_assigned',
  'workflow_logs',
  // Remove review/feedback/rating fields from table (per requirement)
  'ta_remarks',
  'ta_se_quality_feedback',
  'ta_quality_feedback',
  'ta_rating',
  'mis_remarks',
  'mis_validator_qa_feedback',
  'mis_quality_feedback',
  'report_designer_remarks',
  'report_maker_remarks',
  'report_designer_quality_feedback',
  'report_maker_quality_feedback',
  'report_designer_rejection_reason',
  'report_maker_rejection_reason',
  'validator_remarks',
  'validator_report_designer_qa_feedback',
  'validator_quality_feedback',
  'validator_rating',
  // In case API returns this label-like key
  'daily_travel_report',
]);

const EXCLUDED_EXPORT_KEYS = new Set<string>([
  ...Array.from(HIDDEN_TABLE_COLUMNS),
]);

// Keys used internally for file enrichment — never shown in table or export
const COMPUTED_FILE_COUNT_KEY = '__computed_uploaded_file_count__';
const COMPUTED_FILE_SIZE_KEY = '__computed_total_file_size_bytes__';

// ─── File Size Formatter ──────────────────────────────────────────────────────

const formatFileSize = (bytes: unknown): string => {
  if (bytes === null || bytes === undefined || bytes === '') return 'N/A';
  const num = typeof bytes === 'number' ? bytes : Number(bytes);
  if (!Number.isFinite(num) || num < 0) return 'N/A';
  if (num === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(num) / Math.log(1024));
  const index = Math.min(i, units.length - 1);
  const value = num / Math.pow(1024, index);
  return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
};

// ─── Role-based Excel Column Definitions ─────────────────────────────────────
// Maps dot-notation API field paths → user-friendly Excel column labels.
// Covers TA, MIS, Report Designer, Validator review fields + document sizes.

interface RoleColumnDef {
  /** Dot-notation path(s) to look up in the flattened row (first match wins) */
  paths: string[];
  /** Human-readable Excel column header */
  label: string;
  /** Optional transform applied after null-safe extraction */
  transform?: (value: unknown) => string;
}

const ROLE_COLUMN_DEFS: RoleColumnDef[] = [
  // ── Document / File Sizes (computed during export enrichment) ───────────────
  {
    paths: [COMPUTED_FILE_COUNT_KEY],
    label: 'Uploaded Data Files',
  },
  {
    paths: [COMPUTED_FILE_SIZE_KEY],
    label: 'Total Data Document Size',
    transform: formatFileSize,
  },
];

/**
 * Resolves a role-column value from a flattened row.
 * Tries each path in order; returns the first non-null/undefined hit.
 * Falls back to 'N/A'.
 */
const resolveRoleColumnValue = (
  flatRow: Record<string, unknown>,
  def: RoleColumnDef
): string => {
  for (const path of def.paths) {
    const raw = flatRow[path];
    if (raw !== null && raw !== undefined && raw !== '') {
      if (def.transform) return def.transform(raw);
      return formatCellValue(raw) === 'NA' ? 'N/A' : String(formatCellValue(raw));
    }
  }
  return 'N/A';
};

// ─── File Enrichment ─────────────────────────────────────────────────────────
// Fetches uploaded file metadata for a batch of site IDs and returns a map of
// siteId → { count, totalBytes }.  Uses Promise.allSettled so one failed fetch
// never blocks the rest.

interface SiteFileStats {
  count: number;
  totalBytes: number;
}

const fetchFileStatsForSites = async (
  siteIds: number[]
): Promise<Map<number, SiteFileStats>> => {
  const results = new Map<number, SiteFileStats>();
  if (!siteIds.length) return results;

  const settled = await Promise.allSettled(
    siteIds.map(async (siteId) => {
      const res = await axiosInstance.get(`/survey/sites/${siteId}/files`);
      const files: SiteFileEntry[] = res?.data?.data?.files ?? [];
      const count = files.length;
      const totalBytes = files.reduce((sum, f) => {
        const sz = typeof f.file_size === 'number'
          ? f.file_size
          : typeof f.file_size === 'string'
            ? Number(f.file_size)
            : 0;
        return sum + (Number.isFinite(sz) && sz > 0 ? sz : 0);
      }, 0);
      return { siteId, count, totalBytes };
    })
  );

  settled.forEach((result) => {
    if (result.status === 'fulfilled') {
      const { siteId, count, totalBytes } = result.value;
      results.set(siteId, { count, totalBytes });
    }
  });

  return results;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === '[object Object]';

const extractVideoDurationFields = (value: unknown): { details: string; duration: string } => {
  if (value === null || value === undefined || value === '') {
    return { details: 'N/A', duration: 'N/A' };
  }

  const raw = String(value);
  const segments = raw
    .split(/\r?\n|[|]+/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  const detailSegments: string[] = [];
  const durationSegments: string[] = [];

  segments.forEach((segment) => {
    const durationMatch = segment.match(/:\s*([^:]*?\b(?:sec|secs|second|seconds|min|mins|minute|minutes)\b[^:]*)\s*:\s*uploaded at/i);
    if (durationMatch?.[1]) {
      durationSegments.push(durationMatch[1].trim());
      detailSegments.push(segment.replace(durationMatch[0], ': uploaded at'));
      return;
    }
    detailSegments.push(segment);
  });

  return {
    details: detailSegments.join(' | ') || 'N/A',
    duration: durationSegments.join(' | ') || 'N/A',
  };
};

const flattenRow = (value: unknown, prefix = '', out: Record<string, unknown> = {}) => {
  if (!isPlainObject(value)) return out;

  Object.entries(value).forEach(([rawKey, rawValue]) => {
    const key = rawKey.trim();
    if (!key) return;

    if (EXCLUDE_FIELD_SEGMENTS.has(key.toLowerCase())) return;

    // Skip the raw file-details array — the pre-formatted video summary is used instead.
    // uploaded_video_details_summary is remapped so we preserve "Uploaded File Details"
    // while exposing a separate "Video Duration" column.
    if (key === 'uploaded_file_details') return;
    if (key === 'uploaded_video_details_summary') {
      const { details, duration } = extractVideoDurationFields(rawValue);
      const summaryPath = prefix ? `${prefix}.uploaded_file_details` : 'uploaded_file_details';
      const durationPath = prefix ? `${prefix}.video_duration` : 'video_duration';
      out[summaryPath] = details;
      out[durationPath] = duration;
      return;
    }

    const path = prefix ? `${prefix}.${key}` : key;

    if (rawValue === null || rawValue === undefined || typeof rawValue !== 'object') {
      out[path] = rawValue;
      return;
    }

    if (Array.isArray(rawValue)) {
      if (rawValue.length === 0) {
        out[path] = null;
        return;
      }

      const hasNestedObjects = rawValue.some((item) => isPlainObject(item) || Array.isArray(item));
      if (hasNestedObjects) {
        out[path] = JSON.stringify(rawValue);
      } else {
        out[path] = rawValue.join(', ');
      }
      return;
    }

    flattenRow(rawValue, path, out);
  });

  return out;
};

const removeIdFromLabel = (label: string) =>
  label.replace(/\bId\b/g, '').replace(/\s{2,}/g, ' ').trim();

const formatColumnLabel = (columnKey: string) =>
  columnKey
    .split('.')
    .map((segment) => removeIdFromLabel(toReadableLabel(segment)))
    .join(' / ');

const formatCellValue = (value: unknown): string | number => {
  if (value === null || value === undefined || value === '') return 'NA';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? value : 'NA';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'NA';
    return value.map((item) => formatCellValue(item)).join(', ');
  }
  if (isPlainObject(value)) return JSON.stringify(value);
  return String(value);
};

const STATUS_CHIP_CLASS_MAP: Record<string, string> = {
  submitted: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  pending: 'bg-blue-100 text-blue-700',
  draft: 'bg-gray-100 text-gray-700',
  paid: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-rose-100 text-rose-700',
};

const getStatusChipClass = (statusValue: string) => {
  const normalized = statusValue.trim().toLowerCase();
  return STATUS_CHIP_CLASS_MAP[normalized] || 'bg-gray-100 text-gray-700';
};

const isStatusColumn = (column: string) => {
  const key = column.toLowerCase();
  return /(^|[._])status($|[._])/.test(key);
};

const isDateColumn = (column: string) => {
  const normalizedColumn = column.toLowerCase();
  const key = normalizedColumn.split('.').pop() || '';
  return normalizedColumn.startsWith('workflow_status_dates.') || key.endsWith('_at') || key.endsWith('_date') || key === 'date';
};

const toReadableLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const parseMultiValue = (value: string, options: DropdownOption[]) => {
  if (!value) return [];

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((itemValue) => {
      const matched = options.find((option) => String(option.value) === itemValue);
      return matched || { value: itemValue, label: toReadableLabel(itemValue) };
    });
};

const formatDateValue = (value?: string | null, fallback = 'NA') => {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return formatDate(value);
};

interface FilterFormProps {
  filters: SiteReportFilters;
  onChange: (key: keyof SiteReportFilters, value: string) => void;
  onApply: () => void;
  onReset: () => void;
}

interface WorkflowLogsModalProps {
  isOpen: boolean;
  siteLabel: string;
  logs: WorkflowLogEntry[];
  onClose: () => void;
}

const WorkflowLogsModal: React.FC<WorkflowLogsModalProps> = ({ isOpen, siteLabel, logs, onClose }) => {
  const sortedLogs = useMemo(() => {
    return [...logs].sort((a, b) => {
      const first = a.performed_at ? new Date(a.performed_at).getTime() : 0;
      const second = b.performed_at ? new Date(b.performed_at).getTime() : 0;
      return second - first;
    });
  }, [logs]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative min-h-full flex items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-xl border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Workflow Logs</h2>
              <p className="text-sm text-gray-600">{siteLabel}</p>
            </div>
            <button onClick={onClose} className="p-2 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100">
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
          <div className="p-5 max-h-[70vh] overflow-y-auto">
            {sortedLogs.length === 0 ? (
              <p className="text-sm text-gray-600">No workflow logs available.</p>
            ) : (
              <div className="space-y-4">
                {sortedLogs.map((log, index) => (
                  <div key={`${log.performed_at || 'na'}-${index}`} className="relative pl-6">
                    <span className="absolute left-0 top-2 w-2.5 h-2.5 rounded-full bg-blue-600" />
                    {index < sortedLogs.length - 1 && (
                      <span className="absolute left-[4px] top-5 bottom-[-14px] w-px bg-blue-200" />
                    )}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                          {toReadableLabel(log.action_type || 'unknown')}
                        </span>
                        <span className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-700">
                          To: {log.to_status ? toReadableLabel(log.to_status) : 'NA'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">Performed At:</span> {formatDateValue(log.performed_at)}
                      </div>
                      <div className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">Performed By:</span> {formatCellValue(log.performed_by)}
                      </div>
                      <div className="text-sm text-gray-700 bg-white border border-gray-200 rounded p-2">
                        <span className="font-medium text-gray-800">Remarks:</span> {formatCellValue(log.remarks)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterForm: React.FC<FilterFormProps> = ({ filters, onChange, onApply, onReset }) => {
  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectOption | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null);
  const [selectedEngineer, setSelectedEngineer] = useState<SelectOption | null>(null);
  const [selectedStateCircle, setSelectedStateCircle] = useState<SelectOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption | null>(null);

  const surveyWorkflowStatusValues = useMemo(
    () => parseMultiValue(filters.survey_workflow_status_list, WORKFLOW_STATUS_OPTIONS),
    [filters.survey_workflow_status_list]
  );
  const claimStatusValues = useMemo(
    () => parseMultiValue(filters.claim_status_list, CLAIM_STATUS_OPTIONS),
    [filters.claim_status_list]
  );
  const billingStatusValues = useMemo(
    () => parseMultiValue(filters.billing_status_list, BILLING_STATUS_OPTIONS),
    [filters.billing_status_list]
  );

  const selectedStateCircleId = selectedStateCircle
    ? Number(selectedStateCircle.value)
    : filters.state_circle_id
      ? Number(filters.state_circle_id)
      : null;

  useEffect(() => {
    if (!filters.state_circle_id) {
      setSelectedStateCircle(null);
      return;
    }
    setSelectedStateCircle((prev) => {
      if (prev && String(prev.value) === filters.state_circle_id) return prev;
      return {
        id: Number(filters.state_circle_id),
        value: Number(filters.state_circle_id),
        label: `State Circle #${filters.state_circle_id}`,
      };
    });
  }, [filters.state_circle_id]);

  useEffect(() => {
    if (!filters.district_id) {
      setSelectedDistrict(null);
      return;
    }
    setSelectedDistrict((prev) => {
      if (prev && String(prev.value) === filters.district_id) return prev;
      return {
        id: Number(filters.district_id),
        value: Number(filters.district_id),
        label: `District #${filters.district_id}`,
      };
    });
  }, [filters.district_id]);

  useEffect(() => {
    if (!filters.project_id) {
      setSelectedProject(null);
      return;
    }
    setSelectedProject((prev) => {
      if (prev && String(prev.value) === filters.project_id) return prev;
      return {
        id: Number(filters.project_id),
        value: Number(filters.project_id),
        label: `Project #${filters.project_id}`,
      };
    });
  }, [filters.project_id]);

  useEffect(() => {
    if (!filters.department_id) {
      setSelectedDepartment(null);
      return;
    }
    setSelectedDepartment((prev) => {
      if (prev && String(prev.value) === filters.department_id) return prev;
      return {
        id: Number(filters.department_id),
        value: Number(filters.department_id),
        label: `Department #${filters.department_id}`,
      };
    });
  }, [filters.department_id]);

  useEffect(() => {
    if (!filters.activity_id) {
      setSelectedActivity(null);
      return;
    }
    setSelectedActivity((prev) => {
      if (prev && String(prev.value) === filters.activity_id) return prev;
      return {
        id: Number(filters.activity_id),
        value: Number(filters.activity_id),
        label: `Activity #${filters.activity_id}`,
      };
    });
  }, [filters.activity_id]);

  useEffect(() => {
    if (!filters.site_engineer_userid) {
      setSelectedEngineer(null);
      return;
    }
    setSelectedEngineer((prev) => {
      if (prev && String(prev.value) === filters.site_engineer_userid) return prev;
      return {
        id: Number(filters.site_engineer_userid),
        value: Number(filters.site_engineer_userid),
        label: `User #${filters.site_engineer_userid}`,
      };
    });
  }, [filters.site_engineer_userid]);

  const renderTextInput = (
    key: keyof SiteReportFilters,
    label: string,
    placeholder = '',
    type: 'text' | 'number' = 'text'
  ) => (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      <input
        type={type}
        value={filters[key]}
        onChange={(e) => onChange(key, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
      />
    </label>
  );

  // Every date filter is one end of a `<name>_from` / `<name>_to` pair; the
  // picker is bounded by the other end of the same pair.
  const renderDateInput = (key: keyof SiteReportFilters, label: string) => {
    const pair = key.replace(/_(from|to)$/, '');
    const bounds = dateRangeInputBounds(
      filters[`${pair}_from` as keyof SiteReportFilters],
      filters[`${pair}_to` as keyof SiteReportFilters]
    );
    return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      <input
        type="date"
        value={filters[key]}
        onChange={(e) => onChange(key, e.target.value)}
        {...(key.endsWith('_from') ? bounds.from : bounds.to)}
        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
      />
    </label>
    );
  };

  const renderSelect = (
    key: keyof SiteReportFilters,
    label: string,
    options: DropdownOption[]
  ) => (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      <select
        value={filters[key]}
        onChange={(e) => onChange(key, e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );

  const renderMultiSelect = (
    key: keyof SiteReportFilters,
    label: string,
    value: DropdownOption[],
    options: DropdownOption[]
  ) => (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      <SearchableSelect
        value={value}
        onChange={(selected: DropdownOption[]) => {
          const nextValue = (selected || []).map((item) => String(item.value)).join(',');
          onChange(key, nextValue);
        }}
        options={options}
        placeholder={`Select ${label.toLowerCase()}`}
        isMulti
        closeOnOutsideClick={false}
      />
    </label>
  );

  return (
    <div className="bg-white shadow-sm rounded border border-gray-200">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Site Report Filters</h2>
          <p className="text-sm text-gray-600">Apply filters, sorting, and search before loading report fields.</p>
        </div>
      </div>

      <div className="p-4 space-y-6">
            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Search and Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {renderTextInput('search', 'Search', 'airtel')}
                {renderTextInput('site_id', 'Site', 'SIT')}
                {renderTextInput('site_name', 'Site Name', 'Tower')}
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Project</span>
                  <SelectWithSearch
                    placeholder="Search project"
                    value={selectedProject}
                    onChange={(option) => {
                      setSelectedProject(option);
                      onChange('project_id', option ? String(option.value) : '');
                    }}
                    // Scoped on the server to the signed-in user's own projects
                    // (super admin: all), the same rule the report rows follow.
                    apiEndpoint="/reports/site/projects"
                    dataKey="projects"
                    labelKey="project_name"
                    valueKey="id"
                    searchParam="search"
                    closeOnOutsideClick={false}
                    className="w-full"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Department / Project Group</span>
                  <SelectWithSearch
                    placeholder="Search department"
                    value={selectedDepartment}
                    onChange={(option) => {
                      setSelectedDepartment(option);
                      onChange('department_id', option ? String(option.value) : '');
                    }}
                    apiEndpoint="/departments/list"
                    dataKey="departments"
                    labelKey="name"
                    valueKey="id"
                    searchParam="search"
                    extraParams={{ assigned_only: true }}
                    closeOnOutsideClick={false}
                    className="w-full"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Activity</span>
                  <SelectWithSearch
                    placeholder="Search activity"
                    value={selectedActivity}
                    onChange={(option) => {
                      setSelectedActivity(option);
                      onChange('activity_id', option ? String(option.value) : '');
                    }}
                    apiEndpoint="/activities/list"
                    dataKey="activities"
                    labelKey="activity_name"
                    valueKey="id"
                    searchParam="search"
                    closeOnOutsideClick={false}
                    className="w-full"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">State Circle</span>
                  <SelectWithSearch
                    placeholder="Search state circle"
                    value={selectedStateCircle}
                    onChange={(option) => {
                      setSelectedStateCircle(option);
                      setSelectedDistrict(null);
                      onChange('state_circle_id', option ? String(option.value) : '');
                      onChange('district_id', '');
                    }}
                    apiEndpoint="/state-circle/list"
                    dataKey="states"
                    labelKey="state_name"
                    valueKey="id"
                    searchParam="search"
                    closeOnOutsideClick={false}
                    className="w-full"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">District</span>
                  <SelectWithSearch
                    placeholder="Search district"
                    value={selectedDistrict}
                    onChange={(option) => {
                      setSelectedDistrict(option);
                      onChange('district_id', option ? String(option.value) : '');
                    }}
                    apiEndpoint="/state-circle"
                    dataKey="districts"
                    labelKey="name"
                    valueKey="id"
                    searchParam="search"
                    dependencyId={selectedStateCircleId}
                    closeOnOutsideClick={false}
                    className="w-full"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Site Engineer</span>
                  <SelectWithSearch
                    placeholder="Search engineer"
                    value={selectedEngineer}
                    onChange={(option) => {
                      setSelectedEngineer(option);
                      onChange('site_engineer_userid', option ? String(option.value) : '');
                    }}
                    apiEndpoint="/users/list"
                    dataKey="users"
                    labelKey="first_name"
                    valueKey="id"
                    searchParam="search"
                    displayFormat={(item: any) => {
                      const fullName = [item.first_name, item.last_name].filter(Boolean).join(' ').trim();
                      return `${fullName || item.name || `User #${item.id}`}${item.employee_code ? ` (${item.employee_code})` : ''}`;
                    }}
                    closeOnOutsideClick={false}
                    className="w-full"
                  />
                </label>
                {renderMultiSelect('survey_workflow_status_list', 'Survey Workflow Status List', surveyWorkflowStatusValues, WORKFLOW_STATUS_OPTIONS)}
                {renderMultiSelect('claim_status_list', 'Claim Status List', claimStatusValues, CLAIM_STATUS_OPTIONS)}
                {renderMultiSelect('billing_status_list', 'Billing Status List', billingStatusValues, BILLING_STATUS_OPTIONS)}
                {renderSelect(
                  'workflow_to_status',
                  'Workflow To Status',
                  [{ value: '', label: 'All' }, ...WORKFLOW_STATUS_OPTIONS]
                )}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Status and Flags</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {renderSelect('association_type', 'Association Type', [
                  { value: '', label: 'All' },
                  { value: 'inhouse', label: 'OnRole' },
                  { value: 'vendor', label: 'OffRole' },
                ])}
                {renderSelect('travel_mode', 'Travel Mode', [
                  { value: '', label: 'All' },
                  { value: 'self', label: 'Self' },
                  { value: 'cab', label: 'Cab' },
                ])}
                {renderSelect('terrain_type', 'Terrain Type', [
                  { value: '', label: 'All' },
                  { value: 'plain', label: 'Plain' },
                  { value: 'hills', label: 'Hills' },
                ])}
                {renderSelect('wcc_status', 'WCC Status', [{ value: '', label: 'All' }, ...WCC_STATUS_OPTIONS])}
                {renderSelect('invoice_status', 'Invoice Status', [{ value: '', label: 'All' }, ...INVOICE_STATUS_OPTIONS])}
                {renderSelect('workflow_action_type', 'Workflow Action Type', [{ value: '', label: 'All' }, ...WORKFLOW_ACTION_OPTIONS])}
                {renderSelect('is_project_claim_submitted', 'Project Claim Submitted', [
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ])}
                {renderSelect('is_billing_done', 'Billing Done', [
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ])}
                {renderSelect('report_submitted', 'Report Submitted', [
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ])}
                {renderSelect('is_po_received', 'PO Received', [
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ])}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Date Ranges</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {renderDateInput('client_allocation_date_from', 'Client Allocation Date From')}
                {renderDateInput('client_allocation_date_to', 'Client Allocation Date To')}
                {renderDateInput('site_engineer_assign_date_from', 'Site Engineer Assign Date From')}
                {renderDateInput('site_engineer_assign_date_to', 'Site Engineer Assign Date To')}
                {renderDateInput('site_engineer_work_date_from', 'Site Engineer Work Date From')}
                {renderDateInput('site_engineer_work_date_to', 'Site Engineer Work Date To')}
                {renderDateInput('allocation_date_from', 'Allocation Date From')}
                {renderDateInput('allocation_date_to', 'Allocation Date To')}
                {renderDateInput('survey_date_from', 'Survey Date From')}
                {renderDateInput('survey_date_to', 'Survey Date To')}
                {renderDateInput('workflow_performed_at_from', 'Workflow Performed At From')}
                {renderDateInput('workflow_performed_at_to', 'Workflow Performed At To')}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Sorting</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {renderSelect('sort_by', 'Sort By', [
                  { value: 'created_at', label: 'created_at' },
                  { value: 'allocation_date', label: 'allocation_date' },
                  { value: 'survey_date', label: 'survey_date' },
                  { value: 'site_name', label: 'site_name' },
                  { value: 'site_id', label: 'site_id' },
                  { value: 'client_allocation_date', label: 'client_allocation_date' },
                  { value: 'site_engineer_assign_date', label: 'site_engineer_assign_date' },
                  { value: 'site_engineer_work_date', label: 'site_engineer_work_date' },
                ])}
                {renderSelect('sort_order', 'Sort Order', [
                  { value: 'DESC', label: 'DESC' },
                  { value: 'ASC', label: 'ASC' },
                ])}
              </div>
            </section>
          </div>

      <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
        <button
          onClick={onReset}
          className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
        >
          Reset
        </button>
        <button
          onClick={onApply}
          className="px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900"
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

const REPORT_COMPLETED_KEY = 'workflow_status_dates.report_completed';
const FIELD_WORK_COMPLETED_DATE_KEY = 'field_work_completed_date';
const REPORT_MAKER_ROLE_NAMES = ['report maker', 'site designer'];

const SiteReport: React.FC = () => {
  const { user } = useAuth();
  const isReportMaker = REPORT_MAKER_ROLE_NAMES.includes(user?.role?.name?.toLowerCase() || '');

  const [sites, setSites] = useState<SiteReportRow[]>([]);
  // Cache file stats across pages so export can reuse without refetching N+1.
  const [siteFileStatsMap, setSiteFileStatsMap] = useState<Map<number, SiteFileStats>>(new Map());
  const [loadingFileStats, setLoadingFileStats] = useState(false);
  const [pagination, setPagination] = useState<NormalizedPagination>({
    total_items: 0,
    current_page: 1,
    total_pages: 1,
    items_per_page: 50,
    has_next_page: false,
    has_previous_page: false,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [draftFilters, setDraftFilters] = useState<SiteReportFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<SiteReportFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilterForm, setShowFilterForm] = useState(true);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [exportData, setExportData] = useState<Record<string, string | number>[]>([]);
  const [logsModalData, setLogsModalData] = useState<{ siteLabel: string; logs: WorkflowLogEntry[] } | null>(null);

  const activeFiltersCount = useMemo(() => {
    return Object.entries(appliedFilters).filter(([key, value]) => {
      if (!value) return false;
      if (key === 'sort_by' && value === DEFAULT_FILTERS.sort_by) return false;
      if (key === 'sort_order' && value === DEFAULT_FILTERS.sort_order) return false;
      return true;
    }).length;
  }, [appliedFilters]);

  const buildParams = (targetPage: number, targetLimit: number, filters: SiteReportFilters) => {
    const params: Record<string, string | number | boolean> = { page: targetPage, limit: targetLimit };

    (Object.keys(filters) as Array<keyof SiteReportFilters>).forEach((key) => {
      const value = filters[key];
      if (!value) return;

      if (NUMERIC_FILTER_KEYS.includes(key)) {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) {
          params[key] = parsed;
        }
        return;
      }

      if (BOOLEAN_FILTER_KEYS.includes(key)) {
        params[key] = value === 'true';
        return;
      }

      params[key] = value;
    });

    return params;
  };

  const fetchSites = async (
    targetPage: number = page,
    targetLimit: number = pagination.items_per_page,
    filters: SiteReportFilters = appliedFilters
  ) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/reports/site', {
        params: buildParams(targetPage, targetLimit, filters),
      });

      if (response.data.success) {
        const payload = response.data.data || {};
        const rows: SiteReportRow[] = payload.data || [];
        setSites(rows);

        const p = payload.pagination || {};
        const total = Number(p.total ?? p.total_items ?? p.total_count) || rows.length;
        const currentPage = Number(p.current_page) || targetPage;
        const perPage = Number(p.per_page ?? p.items_per_page) || targetLimit;
        const pages = Number(p.total_pages) || Math.max(1, Math.ceil(total / perPage));

        setPagination({
          total_items: total,
          current_page: currentPage,
          total_pages: pages,
          items_per_page: perPage,
          has_next_page: Boolean(p.has_next_page ?? currentPage < pages),
          has_previous_page: Boolean(p.has_prev_page ?? currentPage > 1),
        });

        // Fetch file stats for the loaded page rows (non-blocking)
        const ids = rows
          .map((r) => (typeof r.id === 'number' ? r.id : Number(r.id)))
          .filter((id) => Number.isFinite(id) && id > 0);

        if (ids.length > 0) {
          setLoadingFileStats(true);
          fetchFileStatsForSites(ids)
            .then((statsMap) => {
              setSiteFileStatsMap((prev) => {
                const next = new Map(prev);
                statsMap.forEach((value, key) => next.set(key, value));
                return next;
              });
            })
            .finally(() => setLoadingFileStats(false));
        } else {
          // keep cache (do not wipe)
        }
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch site reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasSearched) return;
    fetchSites(page, pagination.items_per_page, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, hasSearched]);

  const flatSites = useMemo(() => sites.map((site) => flattenRow(site)), [sites]);

  const tableColumns = useMemo(() => {
    const seen = new Set<string>();
    const keys: string[] = [];

    flatSites.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (HIDDEN_TABLE_COLUMNS.has(key)) return;
        // For report makers, REPORT_COMPLETED_KEY is rendered as a dedicated fixed column
        if (isReportMaker && key === REPORT_COMPLETED_KEY) return;
        if (!seen.has(key)) {
          seen.add(key);
          keys.push(key);
        }
      });
    });

    return keys;
  }, [flatSites, isReportMaker]);

  const buildExportRows = (rows: SiteReportRow[], fileStatsMap?: Map<number, SiteFileStats>) => {
    // Inject computed file stats into each raw row before flattening
    const enrichedRows = rows.map((row) => {
      const siteId = typeof row.id === 'number' ? row.id : Number(row.id);
      const stats = fileStatsMap?.get(siteId);
      return {
        ...row,
        [COMPUTED_FILE_COUNT_KEY]: stats ? stats.count : null,
        [COMPUTED_FILE_SIZE_KEY]: stats ? stats.totalBytes : null,
      };
    });

    const flattened = enrichedRows.map((row) => flattenRow(row));
    const seen = new Set<string>();
    const columns: string[] = [];

    flattened.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key === REPORT_COMPLETED_KEY && !isReportMaker) return;
        if (EXCLUDED_EXPORT_KEYS.has(key)) return;
        // Never include computed sentinel keys in the dynamic column list
        if (key === COMPUTED_FILE_COUNT_KEY || key === COMPUTED_FILE_SIZE_KEY) return;
        if (!seen.has(key)) {
          seen.add(key);
          columns.push(key);
        }
      });
    });

    if (isReportMaker) {
      // Ensure field_work_completed_date is at position 1
      const fwcIdx = columns.indexOf(FIELD_WORK_COMPLETED_DATE_KEY);
      if (fwcIdx === -1) {
        columns.splice(1, 0, FIELD_WORK_COMPLETED_DATE_KEY);
      } else if (fwcIdx > 1) {
        columns.splice(fwcIdx, 1);
        columns.splice(1, 0, FIELD_WORK_COMPLETED_DATE_KEY);
      }
      // Ensure report_completed date is at position 2
      const rcIdx = columns.indexOf(REPORT_COMPLETED_KEY);
      if (rcIdx === -1) {
        columns.splice(2, 0, REPORT_COMPLETED_KEY);
      } else if (rcIdx > 2) {
        columns.splice(rcIdx, 1);
        columns.splice(2, 0, REPORT_COMPLETED_KEY);
      }
    }

    // Collect role-column labels that are NOT already covered by a dynamic column
    const dynamicLabels = new Set(columns.map(formatColumnLabel));
    const roleColumnsToAppend = ROLE_COLUMN_DEFS.filter(
      (def) => !dynamicLabels.has(def.label)
    );

    return flattened.map((row) => {
      const exportRow: Record<string, string | number> = {};

      // 1. Dynamic columns from API response
      columns.forEach((column) => {
        const label = formatColumnLabel(column);
        const raw = formatCellValue(row[column]);
        exportRow[label] = (typeof raw === 'string' && raw !== 'NA' && isDateColumn(column))
          ? formatDateValue(raw)
          : raw;
      });

      // 2. Role-based review / rating / file columns (appended at the end)
      roleColumnsToAppend.forEach((def) => {
        exportRow[def.label] = resolveRoleColumnValue(row as Record<string, unknown>, def);
      });

      return exportRow;
    });
  };

  const renderTableCell = (column: string, value: unknown) => {
    const formattedValue = formatCellValue(value);
    if (typeof formattedValue !== 'string') return formattedValue;
    if (formattedValue === 'NA') return formattedValue;

    if (isDateColumn(column)) {
      return formatDateValue(formattedValue);
    }

    if (isStatusColumn(column)) {
      return (
        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium capitalize ${getStatusChipClass(formattedValue)}`}>
          {toReadableLabel(formattedValue)}
        </span>
      );
    }

    return formattedValue;
  };

  const handleFilterChange = (key: keyof SiteReportFilters, value: string) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const dateError = firstDateRangeError([
      [draftFilters.client_allocation_date_from, draftFilters.client_allocation_date_to, 'Client Allocation Date'],
      [draftFilters.site_engineer_assign_date_from, draftFilters.site_engineer_assign_date_to, 'Site Engineer Assign Date'],
      [draftFilters.site_engineer_work_date_from, draftFilters.site_engineer_work_date_to, 'Site Engineer Work Date'],
      [draftFilters.allocation_date_from, draftFilters.allocation_date_to, 'Allocation Date'],
      [draftFilters.survey_date_from, draftFilters.survey_date_to, 'Survey Date'],
      [draftFilters.workflow_performed_at_from, draftFilters.workflow_performed_at_to, 'Workflow Performed At'],
    ]);
    if (dateError) {
      toast.error(dateError);
      return;
    }

    const normalized: SiteReportFilters = {
      ...draftFilters,
      sort_order: draftFilters.sort_order === 'ASC' ? 'ASC' : 'DESC',
      sort_by: draftFilters.sort_by || DEFAULT_FILTERS.sort_by,
    };

    setAppliedFilters(normalized);
    setHasSearched(true);
    setShowFilterForm(false);
    setPage(1);
    fetchSites(1, pagination.items_per_page, normalized);
  };

  const handleResetDraftFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
  };

  const handleClearAppliedFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setHasSearched(false);
    setShowFilterForm(true);
    setSites([]);
    setSiteFileStatsMap(new Map());
    setPage(1);
    setPagination((prev) => ({
      ...prev,
      total_items: 0,
      current_page: 1,
      total_pages: 1,
      has_next_page: false,
      has_previous_page: false,
    }));
  };

  const fetchExportData = async () => {
    try {
      setExporting(true);
      let currentPage = 1;
      let totalPages = 1;
      const pageSize = 100;
      const allRows: SiteReportRow[] = [];

      // Step 1: Fetch all site report rows (paginated)
      do {
        const response = await axiosInstance.get('/reports/site', {
          params: buildParams(currentPage, pageSize, appliedFilters),
        });
        if (!response.data.success) break;
        const payload = response.data.data || {};
        const rows: SiteReportRow[] = payload.data || [];
        allRows.push(...rows);
        const p = payload.pagination || {};
        totalPages =
          Number(p.total_pages) ||
          Math.max(1, Math.ceil((Number(p.total ?? p.total_items ?? p.total_count) || allRows.length) / pageSize));
        currentPage += 1;
      } while (currentPage <= totalPages);

      // Step 2: Collect all valid numeric site IDs for file enrichment
      // Step 2: Build export rows (use cached file stats only; avoid N+1 requests)
      setExportData(buildExportRows(allRows, siteFileStatsMap));
      setShowExcelModal(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to prepare site report export');
    } finally {
      setExporting(false);
    }
  };

  const paginationAdapter: PaginationType = {
    offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
    limit: pagination.items_per_page,
    page: pagination.current_page,
    total_pages: pagination.total_pages,
    total_count: pagination.total_items,
    has_next_page: pagination.has_next_page,
    has_prev_page: pagination.has_previous_page,
    next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
    prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null,
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Site Report</h1>
            <p className="text-sm text-gray-600 mt-1">Apply filters to view all report fields. Null values are shown as NA.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!showFilterForm && (
              <button
                onClick={() => setShowFilterForm(true)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                Apply Filters Again
                {activeFiltersCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={handleClearAppliedFilters}
              className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={fetchExportData}
              disabled={!hasSearched || exporting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              {exporting ? 'Preparing...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {showFilterForm ? (
        <FilterForm
          filters={draftFilters}
          onChange={handleFilterChange}
          onApply={handleApplyFilters}
          onReset={handleResetDraftFilters}
        />
      ) : (
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {tableColumns.length > 0 ? (
                  tableColumns.map((column) => (
                    <th key={column} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      {formatColumnLabel(column)}
                    </th>
                  ))
                ) : (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Site Report</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                  Uploaded Data Files
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                  Total Data Document Size
                </th>
                {isReportMaker && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                    Report Completed Date
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Workflow Logs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={Math.max(tableColumns.length + (isReportMaker ? 4 : 3), 1)} className="px-4 py-10 text-center text-sm text-gray-600">Loading...</td>
                </tr>
              ) : sites.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(tableColumns.length + (isReportMaker ? 4 : 3), 1)} className="px-4 py-10 text-center text-sm text-gray-600">No site reports found.</td>
                </tr>
              ) : (
                sites.map((rawSite, index) => {
                  const site = flatSites[index] || {};
                  const logs: WorkflowLogEntry[] = Array.isArray(rawSite.workflow_logs) ? rawSite.workflow_logs : [];
                  const siteNumId = typeof rawSite.id === 'number' ? rawSite.id : Number(rawSite.id);
                  const fileStats = Number.isFinite(siteNumId) ? siteFileStatsMap.get(siteNumId) : undefined;

                  return (
                    <tr key={`${site.site_id ?? site.reference_number ?? 'row'}-${index}`} className="hover:bg-gray-50">
                      {tableColumns.map((column) => (
                        <td key={column} className="px-4 py-3 text-sm text-gray-700 align-top max-w-sm whitespace-normal break-words">
                          {renderTableCell(column, site[column])}
                        </td>
                      ))}
                      {/* Uploaded Data Files count */}
                      <td className="px-4 py-3 text-sm text-gray-700 align-top whitespace-nowrap">
                        {loadingFileStats ? (
                          <FaSpinner className="w-3 h-3 animate-spin text-gray-400 inline" />
                        ) : fileStats !== undefined ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {fileStats.count} {fileStats.count === 1 ? 'file' : 'files'}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      {/* Total Data Document Size */}
                      <td className="px-4 py-3 text-sm text-gray-700 align-top whitespace-nowrap">
                        {loadingFileStats ? (
                          <FaSpinner className="w-3 h-3 animate-spin text-gray-400 inline" />
                        ) : fileStats !== undefined ? (
                          fileStats.totalBytes > 0 ? (
                            <span className="font-medium text-gray-800">{formatFileSize(fileStats.totalBytes)}</span>
                          ) : (
                            <span className="text-gray-400">0 B</span>
                          )
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      {/* Report Completed Date — report maker only */}
                      {isReportMaker && (
                        <td className="px-4 py-3 text-sm text-gray-700 align-top whitespace-nowrap">
                          {formatDateValue(site[REPORT_COMPLETED_KEY] as string | null | undefined)}
                        </td>
                      )}
                      {/* Workflow Logs */}
                      <td className="px-4 py-3 text-sm text-gray-700 align-top whitespace-nowrap">
                        {logs.length > 0 ? (
                          <button
                            onClick={() =>
                              setLogsModalData({
                                siteLabel: rawSite.site_name || rawSite.site_id || 'Site',
                                logs,
                              })
                            }
                            className="px-3 py-1.5 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            View Logs ({logs.length})
                          </button>
                        ) : (
                          'NA'
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={paginationAdapter}
          onPageChange={(nextPage) => {
            if (!hasSearched) return;
            setPage(nextPage);
          }}
          onLimitChange={(nextLimit) => {
            setPagination((prev) => ({ ...prev, items_per_page: nextLimit }));
            setPage(1);
            if (!hasSearched) return;
            fetchSites(1, nextLimit, appliedFilters);
          }}
        />
      </div>
      )}

      <WorkflowLogsModal
        isOpen={Boolean(logsModalData)}
        siteLabel={logsModalData?.siteLabel || 'Site'}
        logs={logsModalData?.logs || []}
        onClose={() => setLogsModalData(null)}
      />

      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="site_reports"
        title="Export Site Reports"
        description="Download site report records with selected filters"
      />
    </div>
  );
};

export default SiteReport;
